import { getDirectionsFacing } from 'utils/boundingBox';
import { PriorityQueue } from 'utils/priorityQueue';
import { generateMidXBetweenRects, generateMidYBetweenRects } from 'utils/rect';

import { CartesianLine, Line, PointGuidesForSteps, Pos, Rect, Side } from '../LineWidget.types';

/**
 * Calculates the new direction of a specified side of a rectangle or a direction after applying a given rotation.
 *
 * @param {Side} side - The original side of the rectangle.
 * @param {number} rotationDeg - The rotation angle in degrees. This value can be positive or negative,
 *                               representing clockwise or counterclockwise rotation, respectively.
 * @returns {Side} - The new side (or direction) of the rectangle after applying the rotation.
 * @example
 * // Assuming Side.NORTH is 0, Side.EAST is 1, Side.SOUTH is 2, and Side.WEST is 3,
 * // for a 90-degree clockwise rotation from North:
 * getLineDirectionOfSideWithRotation(Side.NORTH, 90); // returns Side.EAST
 */
export const getLineDirectionOfSideWithRotation = (side: Side, rotationDeg: number): Side => {
  // An array of sides in the order: NORTH, EAST, SOUTH, WEST
  const sides = [Side.NORTH, Side.EAST, Side.SOUTH, Side.WEST];

  // Normalizes the rotation degree to a value between 0 and 359
  const normalizedRotation = ((rotationDeg % 360) + 360) % 360;

  // Finds the index of the provided side in the sides array
  const sideIndex = sides.indexOf(side);

  // Calculates the new index after applying the rotation
  const newSideIndex = (sideIndex + Math.floor((normalizedRotation + 45) / 90)) % 4;

  // Returns the side corresponding to the new index
  return sides[newSideIndex];
};

/**
 * Converts the rect positions to be relative to the new origin.
 * For e.x: if rect is at 0,0 initially and new origin is 100,100, then the new rect pos will be -100,-100.
 * @param rect
 * @param newOrigin
 * @returns
 */
export const convertRectPosToNewOriginPos = (rect: Rect, newOrigin: Pos): Rect => {
  return {
    x: parseFloat((rect.x - newOrigin.xPx).toFixed(1)),
    y: parseFloat((rect.y - newOrigin.yPx).toFixed(1)),
    width: parseFloat(rect.width.toFixed(1)),
    height: parseFloat(rect.height.toFixed(1)),
  };
};

export const parseFloatToLinePrecision = (value: number): number => {
  return parseFloat(value.toFixed(1));
};

export const roundPos = (pos: Pos): Pos => {
  return {
    xPx: parseFloat(pos.xPx.toFixed(1)),
    yPx: parseFloat(pos.yPx.toFixed(1)),
  };
};

/**
 * Generates a grid of points from the start and end widgets considering their sides, positions, and an optional margin.
 * It's used only for debug purpose.
 * @param startWidget The starting widget.
 * @param endWidget The ending widget.
 * @param startSide The side of the start widget.
 * @param endSide The side of the end widget.
 * @param startPos The starting position.
 * @param endPos The ending position.
 * @param margin The margin value, defaulting to 10.
 * @returns An object containing the grid points and normal points.
 */
export const generateGridPointsFromWidgets = (
  startRect: Rect,
  endRect: Rect,
  startSide: Side,
  endSide: Side,
  startPos: Pos,
  endPos: Pos,
  margin: number = 10,
): PointGuidesForSteps => {
  const allPoints = generatePoints(startRect, endRect, startSide, endSide, startPos, endPos, margin);
  const pointGuidesForSteps: PointGuidesForSteps = {
    gridPoints: allPoints,
    normalPoints: [],
    points: allPoints,
  };
  return pointGuidesForSteps;
};

/**
 * Generates boundary lines based on the given start and end rectangles, extended by a specified margin.
 *
 * @param {Rect} startRect - The starting rectangle.
 * @param {Rect} endRect - The ending rectangle.
 * @param {number} [margin=10] - The margin by which to extend the boundary lines.
 * @returns {[Line[], Line[]]} An array containing two arrays of Line objects. The first array contains x-axis boundary lines, and the second contains y-axis boundary lines.
 */
export const generateBoundaryLinesAsLines = (startRect: Rect, endRect: Rect, margin: number = 10): [Line[], Line[]] => {
  // Calculate the min and max x and y values from both rectangles
  const minX = Math.min(startRect.x, endRect.x) - margin;
  const maxX = Math.max(startRect.x + startRect.width, endRect.x + endRect.width) + margin;
  const minY = Math.min(startRect.y, endRect.y) - margin;
  const maxY = Math.max(startRect.y + startRect.height, endRect.y + endRect.height) + margin;

  // Create CartesianLine representations
  const xBoundaryLines: Line[] = [
    convertCartesianLineToLine({ axis: 'x', value: minX }),
    convertCartesianLineToLine({ axis: 'x', value: maxX }),
  ];

  const yBoundaryLines: Line[] = [
    convertCartesianLineToLine({ axis: 'y', value: minY }),
    convertCartesianLineToLine({ axis: 'y', value: maxY }),
  ];

  return [xBoundaryLines, yBoundaryLines];
};

/**
 * Generates margin lines for a given rectangle, extended by a specified margin.
 *
 * @param {Rect} rect - The rectangle for which to generate margin lines.
 * @param {number} margin - The margin by which to extend the lines.
 * @returns {[Line[], Line[]]} An array containing two arrays of Line objects. The first array contains x-axis margin lines, and the second contains y-axis margin lines.
 */
export const generateMarginLinesAsLines = (rect: Rect, margin: number): [Line[], Line[]] => {
  // Calculate the new x and y positions and dimensions with margin
  const minX = rect.x - margin;
  const maxX = rect.x + rect.width + margin;
  const minY = rect.y - margin;
  const maxY = rect.y + rect.height + margin;

  // Create CartesianLine representations
  const xMarginLines: Line[] = [
    convertCartesianLineToLine({ axis: 'x', value: minX }),
    convertCartesianLineToLine({ axis: 'x', value: maxX }),
  ];

  const yMarginLines: Line[] = [
    convertCartesianLineToLine({ axis: 'y', value: minY }),
    convertCartesianLineToLine({ axis: 'y', value: maxY }),
  ];

  return [xMarginLines, yMarginLines];
};

/**
 * Compares two position objects to determine if they are equal.
 *
 * @param {Pos} pos1 - The first position object.
 * @param {Pos} pos2 - The second position object.
 * @returns {boolean} Returns true if both x and y coordinates are equal for both positions, false otherwise.
 */
export const isEqualPos = (pos1: Pos, pos2: Pos): boolean => {
  return pos1.xPx === pos2.xPx && pos1.yPx === pos2.yPx;
};

/**
 * Generates a line in a specified direction starting from a given position.
 *
 * @param {Pos} pos - The starting position for the line.
 * @param {Side} direction - The direction in which to extend the line (NORTH, SOUTH, EAST, WEST).
 * @returns {Line} A Line object that starts at the given position and extends infinitely in the specified direction.
 */
export const generateNormalLine = (pos: Pos, direction: Side): Line => {
  let endPos: Pos = { ...pos };

  switch (direction) {
    case Side.NORTH:
      endPos.yPx = -Infinity;
      break;
    case Side.SOUTH:
      endPos.yPx = Infinity;
      break;
    case Side.EAST:
      endPos.xPx = Infinity;
      break;
    case Side.WEST:
      endPos.xPx = -Infinity;
      break;
  }

  return {
    start: pos,
    end: endPos,
  };
};

/**
 * Determines if two lines intersect. The function currently handles only horizontal and vertical lines.
 *
 * @param {Line} line1 - The first line to check for intersection.
 * @param {Line} line2 - The second line to check for intersection.
 * @returns {boolean} Returns true if the lines intersect, false otherwise.
 */
export const doesLinesIntersect = (line1: Line, line2: Line): boolean => {
  // Check for horizontal lines
  if (line1.start.yPx === line1.end.yPx && line2.start.yPx === line2.end.yPx) {
    return false;
  }

  // Check for vertical lines
  if (line1.start.xPx === line1.end.xPx && line2.start.xPx === line2.end.xPx) {
    return false;
  }

  // One line is horizontal and the other is vertical
  if (line1.start.yPx === line1.end.yPx || line2.start.yPx === line2.end.yPx) {
    const horizontalLine = line1.start.yPx === line1.end.yPx ? line1 : line2;
    const verticalLine = line1.start.xPx === line1.end.xPx ? line1 : line2;

    return (
      Math.min(verticalLine.start.yPx, verticalLine.end.yPx) <= horizontalLine.start.yPx &&
      Math.max(verticalLine.start.yPx, verticalLine.end.yPx) >= horizontalLine.start.yPx &&
      Math.min(horizontalLine.start.xPx, horizontalLine.end.xPx) <= verticalLine.start.xPx &&
      Math.max(horizontalLine.start.xPx, horizontalLine.end.xPx) >= verticalLine.start.xPx
    );
  }

  return false; // If lines are not horizontal or vertical, return false
};

/**
 * Calculates the intersection point between a horizontal and a vertical line.
 *
 * @param {Line} horizontal - The horizontal line.
 * @param {Line} vertical - The vertical line.
 * @returns {Pos} Returns the intersection point as a Pos object.
 */
const getIntersectionPoint = (horizontal: Line, vertical: Line): Pos => {
  return { xPx: vertical.start.xPx, yPx: horizontal.start.yPx };
};

/**
 * Checks if an intersection point is blocked by a rectangle based on the projection of a line.
 *
 * @param {Pos} intersection - The intersection point to check.
 * @param {Line} projection - The line projecting the intersection.
 * @param {Rect} rect - The rectangle that might be blocking the intersection.
 * @param {boolean} [isRectEdgeNotBlocking=false] - Flag to indicate if the edge of the rectangle should not block the intersection.
 * @returns {boolean} Returns true if the intersection is blocked, false otherwise.
 */
export const isIntersectionBlockedByRect = (
  intersection: Pos,
  projection: Line,
  rect: Rect,
  isRectEdgeNotBlocking: boolean = false,
): boolean => {
  const lineStartsInsideRect =
    projection.start.xPx > rect.x &&
    projection.start.xPx < rect.x + rect.width &&
    projection.start.yPx > rect.y &&
    projection.start.yPx < rect.y + rect.height;

  if (lineStartsInsideRect) {
    return false;
  }

  if (projection.start.yPx === projection.end.yPx) {
    const direction = projection.start.xPx < projection.end.xPx ? Side.EAST : Side.WEST;

    if (direction === Side.EAST) {
      return (
        (!isRectEdgeNotBlocking &&
          projection.start.yPx >= rect.y &&
          projection.start.yPx <= rect.y + rect.height &&
          projection.start.xPx <= rect.x &&
          intersection.xPx >= rect.x &&
          intersection.xPx >= projection.start.xPx &&
          intersection.xPx <= projection.end.xPx) ||
        (isRectEdgeNotBlocking &&
          projection.start.yPx > rect.y &&
          projection.start.yPx < rect.y + rect.height &&
          projection.start.xPx < rect.x &&
          intersection.xPx > rect.x &&
          intersection.xPx >= projection.start.xPx &&
          intersection.xPx <= projection.end.xPx)
      );
    } else {
      return (
        (!isRectEdgeNotBlocking &&
          projection.start.yPx >= rect.y &&
          projection.start.yPx <= rect.y + rect.height &&
          projection.start.xPx >= rect.x + rect.width &&
          intersection.xPx <= rect.x &&
          intersection.xPx <= projection.start.xPx &&
          intersection.xPx >= projection.end.xPx) ||
        (isRectEdgeNotBlocking &&
          projection.start.yPx > rect.y &&
          projection.start.yPx < rect.y + rect.height &&
          projection.start.xPx > rect.x + rect.width &&
          intersection.xPx < rect.x &&
          intersection.xPx <= projection.start.xPx &&
          intersection.xPx >= projection.end.xPx)
      );
    }
  } else {
    const direction = projection.start.yPx < projection.end.yPx ? Side.SOUTH : Side.NORTH;

    if (direction === Side.SOUTH) {
      return (
        (!isRectEdgeNotBlocking &&
          projection.start.xPx >= rect.x &&
          projection.start.xPx <= rect.x + rect.width &&
          projection.start.yPx <= rect.y &&
          intersection.yPx >= rect.y &&
          intersection.yPx >= projection.start.yPx &&
          intersection.yPx <= projection.end.yPx) ||
        (isRectEdgeNotBlocking &&
          projection.start.xPx > rect.x &&
          projection.start.xPx < rect.x + rect.width &&
          projection.start.yPx < rect.y &&
          intersection.yPx > rect.y &&
          intersection.yPx >= projection.start.yPx &&
          intersection.yPx <= projection.end.yPx)
      );
    } else {
      return (
        (!isRectEdgeNotBlocking &&
          projection.start.xPx >= rect.x &&
          projection.start.xPx <= rect.x + rect.width &&
          projection.start.yPx >= rect.y + rect.height &&
          intersection.yPx <= rect.y + rect.height &&
          intersection.yPx <= projection.start.yPx &&
          intersection.yPx >= projection.end.yPx) ||
        (isRectEdgeNotBlocking &&
          projection.start.xPx > rect.x &&
          projection.start.xPx < rect.x + rect.width &&
          projection.start.yPx > rect.y + rect.height &&
          intersection.yPx < rect.y + rect.height &&
          intersection.yPx <= projection.start.yPx &&
          intersection.yPx >= projection.end.yPx)
      );
    }
  }
};

/**
 * Finds intersection points between a projection line and an array of lines, considering a list of obstacle rectangles.
 *
 * @param {Line} projection - The line to project.
 * @param {Line[]} withLines - An array of lines to check for intersections.
 * @param {Rect[]} obstacles - An array of rectangles that serve as obstacles.
 * @returns {Pos[]} Returns an array of intersection points as Pos objects.
 */
export const getIntersectionsFromProjection = (projection: Line, withLines: Line[], obstacles: Rect[]): Pos[] => {
  const intersections: Pos[] = [];

  for (const line of withLines) {
    if (doesLinesIntersect(projection, line)) {
      const intersectionPoint =
        projection.start.yPx === projection.end.yPx
          ? getIntersectionPoint(projection, line)
          : getIntersectionPoint(line, projection);

      // Check if the intersection point is blocked by any of the obstacles
      let isBlocked = false;
      for (const obstacle of obstacles) {
        if (
          isIntersectionBlockedByRect(intersectionPoint, projection, obstacle) ||
          isIntersectionBlockedByRect(intersectionPoint, line, obstacle)
        ) {
          isBlocked = true;
          break;
        }
      }

      if (isBlocked) {
        continue;
      }

      intersections.push(intersectionPoint);
    }
  }
  return intersections;
};

/**
 * Converts a midline on the x-axis to a Line object extending infinitely along the y-axis.
 *
 * @param {number | null} midXLine - The x-coordinate of the midline, or null.
 * @returns {Line | null} Returns a Line object if the input is a number, or null otherwise.
 */
const convertMidXLineToLine = (midXLine: number | null): Line | null => {
  if (midXLine === null) {
    return null;
  }
  return {
    start: { xPx: midXLine, yPx: -Infinity },
    end: { xPx: midXLine, yPx: Infinity },
  };
};

/**
 * Converts a midline on the y-axis to a Line object extending infinitely along the x-axis.
 *
 * @param {number | null} midYLine - The y-coordinate of the midline, or null.
 * @returns {Line | null} Returns a Line object if the input is a number, or null otherwise.
 */
const convertMidYLineToLine = (midYLine: number | null): Line | null => {
  if (midYLine === null) {
    return null;
  }
  return {
    start: { xPx: -Infinity, yPx: midYLine },
    end: { xPx: Infinity, yPx: midYLine },
  };
};

/**
 * Converts a Cartesian line to a Line object.
 *
 * @param {CartesianLine} cartesianLine - The Cartesian line to convert.
 * @returns {Line} Returns a Line object extending infinitely along the opposite axis.
 */
const convertCartesianLineToLine = (cartesianLine: CartesianLine): Line => {
  if (cartesianLine.axis === 'x') {
    return {
      start: { xPx: cartesianLine.value, yPx: -Infinity },
      end: { xPx: cartesianLine.value, yPx: Infinity },
    };
  } else {
    return {
      start: { xPx: -Infinity, yPx: cartesianLine.value },
      end: { xPx: Infinity, yPx: cartesianLine.value },
    };
  }
};

/**
 * Generates a new position from an existing one, shifted by a margin in a given direction.
 *
 * @param {Pos} pos - The original position.
 * @param {Side} direction - The direction in which to shift.
 * @param {number} margin - The margin by which to shift.
 * @returns {Pos} Returns the new shifted position.
 */
const generateMarginPosFromSide = (pos: Pos, rect: Rect, direction: Side, margin: number): Pos => {
  switch (direction) {
    case Side.NORTH:
      return { xPx: parseFloatToLinePrecision(pos.xPx), yPx: parseFloatToLinePrecision(rect.y - margin) };
    case Side.SOUTH:
      return { xPx: parseFloatToLinePrecision(pos.xPx), yPx: parseFloatToLinePrecision(rect.y + rect.height + margin) };
    case Side.EAST:
      return { xPx: parseFloatToLinePrecision(rect.x + rect.width + margin), yPx: parseFloatToLinePrecision(pos.yPx) };
    case Side.WEST:
      return { xPx: parseFloatToLinePrecision(rect.x - margin), yPx: parseFloatToLinePrecision(pos.yPx) };
    default:
      return { xPx: parseFloatToLinePrecision(pos.xPx), yPx: parseFloatToLinePrecision(rect.x - margin) };
  }
};

/**
 * Recursive function to create intersection points based on various constraints and existing points.
 * It takes into account different parameters like rectangles, lines, and directions.
 * Refer to generatePoints function for use.
 * @param {Pos[]} points - Array of existing points.
 * @param {Side[]} directions - Corresponding directions of the points.
 * @param {Rect} startRect - The starting rectangle.
 * @param {Rect} endRect - The ending rectangle.
 * @param {Line | null} midXLine - The midline along the x-axis.
 * @param {Line | null} midYLine - The midline along the y-axis.
 * @param {Line[]} xBoundaryLines - Boundary lines along the x-axis.
 * @param {Line[]} yBoundaryLines - Boundary lines along the y-axis.
 * @param {Line} thisNormal - The normal line from this object.
 * @param {Line} otherNormal - The normal line from the other object.
 * @param {number} currentStep - The current step in recursion.
 * @param {Pos[]} intersectionsSoFar - The intersections found so far.
 * @param {boolean} [rectsBlockPath=true] - Whether rectangles block the path.
 * @param {number} [margin=10] - The margin for lines.
 * @returns {Pos[]} Returns an array of intersection points.
 */
const createPointsFromPos = (
  points: Pos[],
  directions: Side[],
  startRect: Rect,
  endRect: Rect,
  midXLine: Line | null,
  midYLine: Line | null,
  xBoundaryLines: Line[],
  yBoundaryLines: Line[],
  thisNormal: Line,
  otherNormal: Line,
  currentStep: number,
  intersectionsSoFar: Pos[],
  rectsBlockPath: boolean = true,
  margin: number = 10,
): Pos[] => {
  if (currentStep > 3) {
    return intersectionsSoFar;
  }
  const [startRectXMarginLines, startRectYMarginLines] = generateMarginLinesAsLines(startRect, margin);
  const [endRectXMarginLines, endRectYMarginLines] = generateMarginLinesAsLines(endRect, margin);
  const marginLines = [
    ...startRectXMarginLines,
    ...startRectYMarginLines,
    ...endRectXMarginLines,
    ...endRectYMarginLines,
  ];

  // If projecting line from rect, then apply margin if needed
  const nonNullMidLines = [midXLine, midYLine].filter((line) => line !== null) as Line[];

  if (currentStep === 1) {
    const point = points[0];
    const direction = directions[0];
    const projectionLine = generateNormalLine(point, direction);
    const lineIntersectionsWithNormal = getIntersectionsFromProjection(
      projectionLine,
      [otherNormal],
      [endRect, startRect],
    );
    const lineIntersectionsWithMidLines = getIntersectionsFromProjection(projectionLine, nonNullMidLines, [
      endRect,
      startRect,
    ]);
    const lineIntersectionsWithBoundary = getIntersectionsFromProjection(
      projectionLine,
      [...xBoundaryLines, ...yBoundaryLines],
      [endRect, startRect],
    );
    const marginPoint = generateMarginPosFromSide(point, startRect, direction, margin);
    if (lineIntersectionsWithNormal.length > 0) {
      return [...intersectionsSoFar, ...lineIntersectionsWithNormal];
    } else if (lineIntersectionsWithMidLines.length > 0) {
      return [...intersectionsSoFar, ...lineIntersectionsWithMidLines, ...lineIntersectionsWithBoundary];
    } else {
      // if it doesn't hit a normal or midline, then it must hit a boundary (unless it hits the rect, TODO: check this case)
      let newPoints = [marginPoint];
      let newDirections = [direction];
      let newIntersectionsSoFar = [...intersectionsSoFar, marginPoint];
      if (lineIntersectionsWithBoundary.length > 0) {
        newPoints.push(lineIntersectionsWithBoundary[0]);
        newDirections.push(direction);
        newIntersectionsSoFar.push(lineIntersectionsWithBoundary[0]);
      }
      return createPointsFromPos(
        newPoints,
        newDirections,
        startRect,
        endRect,
        midXLine,
        midYLine,
        xBoundaryLines,
        yBoundaryLines,
        thisNormal,
        otherNormal,
        currentStep + 1,
        newIntersectionsSoFar,
        rectsBlockPath,
        margin,
      );
    }
  } else {
    let addedIntersections: Pos[] = [];
    // current step > 1
    for (let i = 0; i < points.length; i++) {
      // For each point, check left/right projection from the point.
      // If one of the left/right intersects with a normal or midline, then only project
      // the line that hit the midline/normal.
      const point = points[i];
      const [leftDirection, rightDirection] = getDirectionsFacing(directions[i]);

      const leftProjection = generateNormalLine(point, leftDirection);
      const rightProjection = generateNormalLine(point, rightDirection);

      const leftLineIntersectionsWithNormal = getIntersectionsFromProjection(
        leftProjection,
        [otherNormal],
        [endRect, startRect],
      );
      const leftLineIntersectionsWithMidLines = getIntersectionsFromProjection(leftProjection, nonNullMidLines, [
        endRect,
        startRect,
      ]);
      const leftLineIntersectionsWithBoundary = getIntersectionsFromProjection(
        leftProjection,
        [...xBoundaryLines, ...yBoundaryLines],
        [endRect, startRect],
      );
      const leftLineIntersectionsWithMargin = getIntersectionsFromProjection(leftProjection, marginLines, [
        endRect,
        startRect,
      ]);

      const rightLineIntersectionsWithNormal = getIntersectionsFromProjection(
        rightProjection,
        [otherNormal],
        [endRect, startRect],
      );
      const rightLineIntersectionsWithMidLines = getIntersectionsFromProjection(rightProjection, nonNullMidLines, [
        endRect,
        startRect,
      ]);
      const rightLineIntersectionsWithBoundary = getIntersectionsFromProjection(
        rightProjection,
        [...xBoundaryLines, ...yBoundaryLines],
        [endRect, startRect],
      );
      const rightLineIntersectionsWithMargin = getIntersectionsFromProjection(rightProjection, marginLines, [
        endRect,
        startRect,
      ]);

      if (leftLineIntersectionsWithNormal.length > 0 || leftLineIntersectionsWithMidLines.length > 0) {
        // If there is an intersection with a normal or midline, then do not bother projecting the other line.
        if (leftLineIntersectionsWithNormal.length > 0) {
          addedIntersections = [...addedIntersections, ...leftLineIntersectionsWithNormal];
        } else {
          addedIntersections = [
            ...addedIntersections,
            ...leftLineIntersectionsWithMidLines,
            ...leftLineIntersectionsWithBoundary,
          ];
        }
      } else if (rightLineIntersectionsWithNormal.length > 0 || rightLineIntersectionsWithMidLines.length > 0) {
        // If there is an intersection with a normal or midline, then do not bother projecting the other line.
        if (rightLineIntersectionsWithNormal.length > 0) {
          addedIntersections = [...addedIntersections, ...rightLineIntersectionsWithNormal];
        } else {
          addedIntersections = [
            ...addedIntersections,
            ...rightLineIntersectionsWithMidLines,
            ...rightLineIntersectionsWithBoundary,
          ];
        }
      } else {
        // If neither left or right line intersects with a normal or midline, then project both lines to hit boundary or margin+boundary.
        let newPoints = [];
        let newDirections = [];
        let newIntersectionsSoFar = [...intersectionsSoFar];

        if (leftLineIntersectionsWithBoundary.length > 0) {
          newPoints.push(leftLineIntersectionsWithBoundary[0]);
          newDirections.push(leftDirection);
          newIntersectionsSoFar.push(leftLineIntersectionsWithBoundary[0]);

          if (
            leftLineIntersectionsWithMargin.length > 0 &&
            !isEqualPos(leftLineIntersectionsWithBoundary[0], leftLineIntersectionsWithMargin[0])
          ) {
            newPoints.push(leftLineIntersectionsWithMargin[0]);
            newDirections.push(leftDirection);
            newIntersectionsSoFar.push(leftLineIntersectionsWithMargin[0]);
          }
        }
        if (rightLineIntersectionsWithBoundary.length > 0) {
          newPoints.push(rightLineIntersectionsWithBoundary[0]);
          newDirections.push(rightDirection);
          newIntersectionsSoFar.push(rightLineIntersectionsWithBoundary[0]);

          if (
            rightLineIntersectionsWithMargin.length > 0 &&
            !isEqualPos(rightLineIntersectionsWithBoundary[0], rightLineIntersectionsWithMargin[0])
          ) {
            newPoints.push(rightLineIntersectionsWithMargin[0]);
            newDirections.push(rightDirection);
            newIntersectionsSoFar.push(rightLineIntersectionsWithMargin[0]);
          }
        }

        if (newPoints.length > 0) {
          addedIntersections = [
            ...addedIntersections,
            ...createPointsFromPos(
              newPoints,
              newDirections,
              startRect,
              endRect,
              midXLine,
              midYLine,
              xBoundaryLines,
              yBoundaryLines,
              thisNormal,
              otherNormal,
              currentStep + 1,
              newIntersectionsSoFar,
              rectsBlockPath,
              margin,
            ),
          ];
        }
      }
    }

    // return all the intersections from both left and right projections
    return [...intersectionsSoFar, ...addedIntersections];
  }
};

/**
 * Generates a unique set of intersection points based on starting and ending rectangles, their sides, and positions.
 * The function takes into account various parameters like margins and whether rectangles block the path.
 * NOTE: Refer to https://venngage.atlassian.net/l/cp/1TmwNwwf for the algorithm.
 * @param {Rect} startRect - The starting rectangle.
 * @param {Rect} endRect - The ending rectangle.
 * @param {Side} startSide - The side of the starting rectangle.
 * @param {Side} endSide - The side of the ending rectangle.
 * @param {Pos} startPos - The starting position.
 * @param {Pos} endPos - The ending position.
 * @param {number} [margin=10] - The margin for lines.
 * @param {boolean} [rectsBlockPath=true] - Whether rectangles block the path.
 * @returns {Pos[]} Returns an array of unique intersection points.
 */
export const generatePoints = (
  startRect: Rect,
  endRect: Rect,
  startSide: Side,
  endSide: Side,
  startPos: Pos,
  endPos: Pos,
  margin: number = 10,
  rectsBlockPath: boolean = true,
): Pos[] => {
  const startNormalLine = generateNormalLine(startPos, startSide);

  const endNormalLine = generateNormalLine(endPos, endSide);
  const midXLine = generateMidXBetweenRects(startRect, endRect);
  const midYLine = generateMidYBetweenRects(startRect, endRect);
  const [xBoundaryLines, yBoundaryLines] = generateBoundaryLinesAsLines(startRect, endRect, margin);

  // Create points from start rect
  const pointsFromStart = createPointsFromPos(
    [startPos],
    [startSide],
    startRect,
    endRect,
    convertMidXLineToLine(midXLine),
    convertMidYLineToLine(midYLine),
    xBoundaryLines,
    yBoundaryLines,
    startNormalLine,
    endNormalLine,
    1,
    [startPos],
    rectsBlockPath,
    margin,
  );

  // Create points from end rect
  const pointsFromEnd = createPointsFromPos(
    [endPos],
    [endSide],
    endRect,
    startRect,
    convertMidXLineToLine(midXLine),
    convertMidYLineToLine(midYLine),
    xBoundaryLines,
    yBoundaryLines,
    endNormalLine,
    startNormalLine,
    1,
    [endPos],
    rectsBlockPath,
    margin,
  );

  // Add all points to unique array
  const allPoints: Pos[] = [];
  const uniquePointsSet = new Set<string>();
  const addUniquePoint = (point: Pos) => {
    const pointString = `${point.xPx},${point.yPx}`;
    if (!uniquePointsSet.has(pointString)) {
      uniquePointsSet.add(pointString);
      allPoints.push(point);
    }
  };
  pointsFromStart.forEach(addUniquePoint);
  pointsFromEnd.forEach(addUniquePoint);
  return allPoints;
};

/**
 * Checks if a given point exists in a list of points.
 *
 * @param pointToCheck The point to be checked
 * @param pointsList An array of points
 * @returns A boolean value indicating whether the given point exists in the list of points.
 */
export const containsPoint = (pointToCheck: Pos, pointsList: Pos[]) => {
  return pointsList.some((point) => point.xPx === pointToCheck.xPx && point.yPx === pointToCheck.yPx);
};

/**
 * Generates a graph from an array of points. Edges are created between points
 * that share the same x or y coordinate. If a rectangle is blocking the path,
 * the connection is not made.
 *
 * @param {Pos[]} points - An array of points with xPx and yPx properties.
 * @param {Rect} rect1 - The first rectangle to check for blocking.
 * @param {Rect} rect2 - The second rectangle to check for blocking.
 * @returns {Graph} A graph object where keys represent coordinates and values are arrays of neighboring points.
 * @example
 * INPUT:
 * const points = [
 *   { xPx: 1, yPx: 1 },
 *   { xPx: 1, yPx: 2 },
 *   { xPx: 2, yPx: 1 }
 * ];
 *
 * OUTPUT:
 * {
 *   "1,1": [{point: { xPx: 1, yPx: 2 }, distance: num}, {point: { xPx: 2, yPx: 1 }, distance: num}],
 *   "1,2": [{point: { xPx: 1, yPx: 1 }, distance: num}],
 *   "2,1": [{point: { xPx: 1, yPx: 1 }, distance: num}]
 * }
 */

// Function to calculate distance
const calculatePosDistance = (a: Pos, b: Pos) =>
  parseFloat((Math.abs(a.xPx - b.xPx) + Math.abs(a.yPx - b.yPx)).toFixed(2));
export const generateGraphFromPoints = (points: Pos[], rect1: Rect, rect2: Rect, startPos: Pos, endPos: Pos): Graph => {
  // Group points by x and y coordinates
  const groupedByX: Record<number, Pos[]> = {};
  const groupedByY: Record<number, Pos[]> = {};

  for (const point of points) {
    groupedByX[point.xPx] = (groupedByX[point.xPx] || []).concat(point);
    groupedByY[point.yPx] = (groupedByY[point.yPx] || []).concat(point);
  }

  // Create edges between points that share the same x or y coordinate
  const graph: Graph = {};

  const createLineFromPositions = (start: Pos, end: Pos): Line => ({
    start,
    end,
  });

  // Connect points with the same x coordinate if no rect in the way
  for (const xGroup of Object.values(groupedByX)) {
    if (xGroup.length > 1) {
      xGroup.sort((a, b) => a.yPx - b.yPx);
      for (let i = 0; i < xGroup.length; i++) {
        const pointKey = `${xGroup[i].xPx},${xGroup[i].yPx}`;
        graph[pointKey] = graph[pointKey] || [];
        if (i > 0) {
          const line = createLineFromPositions(xGroup[i], xGroup[i - 1]);
          if (
            (!isIntersectionBlockedByRect(xGroup[i - 1], line, rect1, true) &&
              !isIntersectionBlockedByRect(xGroup[i - 1], line, rect2, true)) ||
            containsPoint(xGroup[i], [startPos, endPos]) ||
            containsPoint(xGroup[i - 1], [startPos, endPos])
          ) {
            graph[pointKey].push({ point: xGroup[i - 1], distance: calculatePosDistance(xGroup[i], xGroup[i - 1]) });
          }
        }

        if (i < xGroup.length - 1) {
          const line = createLineFromPositions(xGroup[i], xGroup[i + 1]);
          if (
            (!isIntersectionBlockedByRect(xGroup[i + 1], line, rect1, true) &&
              !isIntersectionBlockedByRect(xGroup[i + 1], line, rect2, true)) ||
            containsPoint(xGroup[i], [startPos, endPos]) ||
            containsPoint(xGroup[i + 1], [startPos, endPos])
          ) {
            graph[pointKey].push({ point: xGroup[i + 1], distance: calculatePosDistance(xGroup[i], xGroup[i + 1]) });
          }
        }
      }
    }
  }

  // Connect points with the same y coordinate if no rect in the way.
  for (const yGroup of Object.values(groupedByY)) {
    if (yGroup.length > 1) {
      yGroup.sort((a, b) => a.xPx - b.xPx);
      for (let i = 0; i < yGroup.length; i++) {
        const pointKey = `${yGroup[i].xPx},${yGroup[i].yPx}`;
        graph[pointKey] = graph[pointKey] || [];

        if (i > 0) {
          const line = createLineFromPositions(yGroup[i], yGroup[i - 1]);
          if (
            (!isIntersectionBlockedByRect(yGroup[i - 1], line, rect1, true) &&
              !isIntersectionBlockedByRect(yGroup[i - 1], line, rect2, true)) ||
            containsPoint(yGroup[i], [startPos, endPos]) ||
            containsPoint(yGroup[i - 1], [startPos, endPos])
          ) {
            graph[pointKey].push({ point: yGroup[i - 1], distance: calculatePosDistance(yGroup[i], yGroup[i - 1]) });
          }
        }

        if (i < yGroup.length - 1) {
          const line = createLineFromPositions(yGroup[i], yGroup[i + 1]);
          if (
            (!isIntersectionBlockedByRect(yGroup[i + 1], line, rect1, true) &&
              !isIntersectionBlockedByRect(yGroup[i + 1], line, rect2, true)) ||
            containsPoint(yGroup[i], [startPos, endPos]) ||
            containsPoint(yGroup[i + 1], [startPos, endPos])
          ) {
            graph[pointKey].push({ point: yGroup[i + 1], distance: calculatePosDistance(yGroup[i], yGroup[i + 1]) });
          }
        }
      }
    }
  }

  return graph;
};

/**
 * Optimizes a given path by removing redundant points in straight lines.
 *
 * @param {Pos[]} path - An array of points forming the path.
 * @returns {Pos[]} An optimized array of points.
 */
export const combineStraightPaths = (path: Pos[]): Pos[] => {
  if (path.length < 3) return path; // Can't optimize paths shorter than 3 points

  const optimizedPath: Pos[] = [path[0]];

  for (let i = 1; i < path.length - 1; i++) {
    const prev = path[i - 1];
    const curr = path[i];
    const next = path[i + 1];

    const sameXPrevCurr = prev.xPx === curr.xPx;
    const sameXCurrNext = curr.xPx === next.xPx;
    const sameYPrevCurr = prev.yPx === curr.yPx;
    const sameYCurrNext = curr.yPx === next.yPx;

    if ((sameXPrevCurr && sameXCurrNext) || (sameYPrevCurr && sameYCurrNext)) {
      // Skip this point because the path remains straight
      continue;
    }

    optimizedPath.push(curr);
  }

  optimizedPath.push(path[path.length - 1]); // Always include the last point
  return optimizedPath;
};

/**
 * Determines if two numbers are within a certain small range (epsilon) of each other.
 * This function is used to compare floating point numbers where small differences
 * are considered insignificant.
 *
 * @param {number} a - The first number to compare.
 * @param {number} b - The second number to compare.
 * @returns {boolean} - Returns `true` if the difference between `a` and `b` is less than the epsilon value, otherwise `false`.
 */
export const isClosePositionValue = (a: number, b: number) => {
  const EPSILON = 1.5;
  return Math.abs(a - b) < EPSILON;
};

/**
 * Processes an array of positions (`Pos`) to remove miniscule steps in the path.
 * The function iterates over the path and adjusts positions to align them along either the x or y axis,
 * if the subsequent positions are close enough (determined by `isClosePositionValue`). This process
 * effectively straightens out small deviations in the path.
 *
 * @param {Pos[]} path - An array of position objects, where each object has `xPx` and `yPx` properties representing coordinates in pixels.
 * @returns {Pos[]} - Returns a new array of position objects (`Pos`) where miniscule steps have been adjusted to create a more aligned path.
 *
 */
export const removeMinisculeSteps = (path: Pos[]): Pos[] => {
  // first make a copy of path
  const newPath = [...path];
  for (let i = 0; i + 3 <= newPath.length - 1; i += 2) {
    const curr = newPath[i];
    const next = newPath[i + 1];
    const next2 = newPath[i + 2];
    const next3 = newPath[i + 3];

    const currToNextDirection = curr.xPx === next.xPx ? 'y' : 'x';

    // currToNext is horizontal, check if curr2 has nearly the same y value.
    if (currToNextDirection === 'x') {
      if (isClosePositionValue(curr.yPx, next2.yPx)) {
        // change next2 and next3 y position to be same as curr
        newPath[i + 2] = { xPx: next2.xPx, yPx: curr.yPx };
        newPath[i + 3] = { xPx: next3.xPx, yPx: curr.yPx };
      }
    } else {
      // currToNext is vertical, check if curr2 has nearly the same x value.
      if (isClosePositionValue(curr.xPx, next2.xPx)) {
        // change next2 and next3 x position to be same as curr
        newPath[i + 2] = { xPx: curr.xPx, yPx: next2.yPx };
        newPath[i + 3] = { xPx: curr.xPx, yPx: next3.yPx };
      }
    }
  }
  return newPath;
};

/**
 * Generates an optimal steps path between a starting and an ending position, considering rectangles and sides.
 *
 * @param {Pos} startPos - The starting position.
 * @param {Pos} endPos - The ending position.
 * @param {Side} startSide - The side of the starting rectangle.
 * @param {Side} endSide - The side of the ending rectangle.
 * @param {Rect} startRect - The starting rectangle.
 * @param {Rect} endRect - The ending rectangle.
 * @param {number} margin - The margin for lines.
 * @param {number} startRotation - The rotation of the starting rectangle
 * @returns {Pos[]} An array of points forming the optimal path.
 */
export const generateStepsPath = (
  startPos: Pos,
  endPos: Pos,
  startSide: Side,
  endSide: Side,
  startRect: Rect,
  endRect: Rect,
  margin: number,
): Pos[] => {
  const points = generatePoints(
    startRect,
    endRect,
    startSide,
    endSide,
    startPos,
    endPos,
    parseFloatToLinePrecision(margin),
  );
  const graph = generateGraphFromPoints(points, startRect, endRect, startPos, endPos);
  const optimalPath = findOptimalStepsPath(startPos, endPos, graph);
  if (optimalPath.length < 2) {
    return [];
  }

  const simplifiedPath = combineStraightPaths(optimalPath);
  const roundPaths = removeMinisculeSteps(simplifiedPath);
  return roundPaths;
};

/**
 * Generates default starting and ending sides based on the relative positions of the start and end points.
 *
 * @param {Pos} startPos - The starting position.
 * @param {Pos} endPos - The ending position.
 * @returns {[Side, Side]} An array containing the default starting and ending sides.
 */
export const generateDefaultStartEndSides = (startPos: Pos, endPos: Pos): [Side, Side] => {
  const xDifference = Math.abs(endPos.xPx - startPos.xPx);
  const yDifference = Math.abs(endPos.yPx - startPos.yPx);

  if (xDifference > yDifference) {
    return startPos.xPx < endPos.xPx ? [Side.EAST, Side.WEST] : [Side.WEST, Side.EAST];
  } else {
    return startPos.yPx < endPos.yPx ? [Side.SOUTH, Side.NORTH] : [Side.NORTH, Side.SOUTH];
  }
};

/**
 * Represents a graph where keys are coordinates in the format "x,y",
 * and values are arrays of objects containing a point and the distance to that point.
 */
export interface Graph {
  [key: string]: { point: Pos; distance: number }[];
}

/**
 * Determines if there is a change in direction between three points.
 *
 * @param prev - The previous point.
 * @param current - The current point.
 * @param neighbor - The neighboring point to evaluate.
 * @returns True if there is a change in direction, false otherwise.
 * @example
 * const prev: Pos = { xPx: 1, yPx: 1 };
 * const current: Pos = { xPx: 1, yPx: 2 };
 * const neighbor: Pos = { xPx: 2, yPx: 2 };
 * const stepChange = isStepChange(prev, current, neighbor);  // stepChange will be true
 */
const isStepChange = (prev: Pos | undefined, current: Pos, neighbor: Pos): boolean => {
  if (!prev) return true; // A step change is assumed if 'prev' is undefined.

  const prevToCurrentIsHorizontal = prev.xPx === current.xPx;
  const prevToCurrentIsVertical = prev.yPx === current.yPx;

  const currentToNeighborIsHorizontal = current.xPx === neighbor.xPx;
  const currentToNeighborIsVertical = current.yPx === neighbor.yPx;

  // Return true if the directions are different, false otherwise.
  return (
    !(prevToCurrentIsHorizontal && currentToNeighborIsHorizontal) &&
    !(prevToCurrentIsVertical && currentToNeighborIsVertical)
  );
};

/**
 * Finds the optimal steps path between a starting and an ending position using Dijkstra's algorithm.
 * This function uses a custom priority queue and step changes to optimize the path.
 *
 * @param {Pos} startPos - The starting position.
 * @param {Pos} endPos - The ending position.
 * @param {Graph} graph - The graph representing possible paths.
 * @returns {Pos[]} An array of points forming the optimal steps path.
 */
export const findOptimalStepsPath = (startPos: Pos, endPos: Pos, graph: Graph): Pos[] => {
  // Initialize dictionaries for distances, previous points, and steps
  const dist: { [key: string]: number } = {};
  const prev: { [key: string]: Pos } = {};
  const steps: { [key: string]: number } = {};

  // Keep track of visited points
  const visited: Set<string> = new Set();

  // Initialize priority queue with custom comparison function
  const pq = new PriorityQueue<Pos>((a, b) => {
    const keyA = `${a.xPx},${a.yPx}`;
    const keyB = `${b.xPx},${b.yPx}`;
    if (dist[keyA] === dist[keyB]) {
      return steps[keyA] - steps[keyB]; // Compare by steps if distances are the same
    }
    return dist[keyA] - dist[keyB]; // Compare by distance otherwise
  });

  // Create string keys for start and end positions
  const startKey = `${startPos.xPx},${startPos.yPx}`;
  const endKey = `${endPos.xPx},${endPos.yPx}`;

  // Initialize Dijkstra's algorithm
  dist[startKey] = 0;
  steps[startKey] = 0;
  pq.push(startPos);

  let counter = 0;
  const maxIterations = 200;

  // Main Dijkstra's loop
  while (!pq.isEmpty()) {
    counter++;
    if (counter > maxIterations) {
      // Add this block to limit iterations in case of infinite loop.
      return [startPos, endPos];
    }

    const current = pq.pop();
    const currentKey = `${current.xPx},${current.yPx}`;

    // Skip visited points
    if (visited.has(currentKey)) continue;

    // Break if we reach the end
    if (currentKey === endKey) break;

    // Check all neighbors
    if (!graph[currentKey]) {
      console.log("graph doesn't have key", currentKey);
    } else {
      for (const { point: neighbor, distance } of graph[currentKey]) {
        const neighborKey = `${neighbor.xPx},${neighbor.yPx}`;

        // Skip if already visited
        if (visited.has(neighborKey)) continue;

        // Calculate new distance and steps
        const newDistance = dist[currentKey] + distance;
        const newSteps = steps[currentKey] + (isStepChange(prev[currentKey], current, neighbor) ? 1 : 0);

        // Update if this path is better
        if (
          newDistance < (dist[neighborKey] || Infinity) ||
          (newDistance === (dist[neighborKey] || Infinity) && newSteps < (steps[neighborKey] || Infinity))
        ) {
          dist[neighborKey] = newDistance;
          steps[neighborKey] = newSteps;
          prev[neighborKey] = current;
          pq.push(neighbor);
        }
      }

      // Mark as visited
      visited.add(currentKey);
    }
  }

  // Reconstruct the path from end to start by backtracking
  const path: Pos[] = [];
  let current: Pos | null = endPos;

  let unshiftCounter = 0;

  while (current) {
    // something went wrong, so just return the start and end positions
    if (unshiftCounter >= maxIterations) {
      return [startPos, endPos];
    }
    path.unshift(current);
    current = prev[`${current.xPx},${current.yPx}`] || null;
    unshiftCounter++;
  }

  return path;
};
